import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Typography, TextField, Button, Grid, Box } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import "./update-password.css";
import Loading from "../../../common/loader/loading";

const validationSchema = Yup.object().shape({
  password: Yup.string().min(8, "Password must be at least 8 characters").required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const UpdatePassword = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { email } = location.state;

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`/api/auth/users/${email}`);
        setUserData(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchUserData();
  }, [email]);

  if (loading) {
    return <Loading />;
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    const token = userData?.resetpasswordtoken;

    try {
      await axios.post(
        `/api/auth/reset-password/${token}`,
        { password: values.password },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      setError("");
      setTimeout(() => {
        navigate("/reset-success");
      }, 3000);
    } catch (err) {
      setError("Failed to reset password. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Grid container justifyContent='center' alignItems='center' className='update-password-container'>
      <Grid item xs={12} sm={8} md={4}>
        <Box className='update-password-form' p={4}>
          <img src='/images/reset.png' alt='' className='lock' />

          <Typography variant='h5' align='center' gutterBottom>
            Create a new <br />
            password
          </Typography>
          <Typography variant='body1' align='center' gutterBottom>
            Create a secure new password after completing verification. Ensure it meets the required security criteria.
          </Typography>
          <Formik
            initialValues={{ password: "", confirmPassword: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, errors, touched, handleChange }) => (
              <Form>
                {error && <Typography color='error'>{error}</Typography>}

                <Field
                  as={TextField}
                  name='password'
                  type='password'
                  label='Set New Password'
                  fullWidth
                  margin='normal'
                  onChange={handleChange}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white", // Your desired background color
                    },
                  }}
                />

                <Field
                  as={TextField}
                  name='confirmPassword'
                  type='password'
                  label='Confirm New Password'
                  fullWidth
                  margin='normal'
                  onChange={handleChange}
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white", // Your desired background color
                    },
                  }}
                />
                <Grid className='submit-button'>
                  <Button type='submit' variant='contained' color='primary' fullWidth disabled={isSubmitting}>
                    {isSubmitting ? "Resetting..." : "Reset Password"}
                  </Button>
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </Grid>
  );
};

export default UpdatePassword;
